import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Download } from "lucide-react";
import { useMemo } from "react";

import { type ResourceConfig, ResourcePage } from "@/components/resource-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/services/api";
import { listCursor } from "@/utils/geo";
import type { Translate } from "@/constants/i18n";
import { useTranslation } from "@/store/locale";

interface LogEntry {
  id: number;
  employee_no: string;
  full_name: string;
  changes: {
    before: Record<string, string | null>;
    after: Record<string, string | null>;
  } | null;
  system_user: string;
  system_user_id: number;
  handle_user: string;
  handle_user_id: number;
  reason: string;
  status: "done" | "pending" | "rejected";
  created_at: string;
}

interface Options {
  status: { options: { value: string; label: string }[] };
  reason: { options: { value: string; label: string }[] };
}

const options = (signal?: AbortSignal) =>
  api
    .get<Options>("/employee-activity-logs/options", signal ? { signal } : {})
    .then((r) => r.data);
const label = (kind: keyof Options) => async () => (await options())[kind].options;

const TONE: Record<LogEntry["status"], "success" | "elevated" | "danger"> = {
  done: "success",
  pending: "elevated",
  rejected: "danger",
};

const build = (t: Translate): ResourceConfig<LogEntry> => ({
  title: t("page.activityLog"),
  singular: t("log.entry"),
  endpoint: "/employee-activity-logs",
  backTo: "/employees",
  readOnly: true,
  noView: true,
  // Every event ever raised: it scrolls back through time, and nobody counts it.
  infinite: true,
  searchPlaceholder: t("log.employeeSearch"),
  columns: [
    {
      key: "employee",
      label: t("log.employee"),
      fields: ["employee_no", "full_name"],
      render: (r) => (
        <span className="block min-w-0">
          <span className="text-fg block font-medium">{r.full_name}</span>
          <span className="text-fg-muted text-footnote block tabular-nums">
            {r.employee_no}
          </span>
        </span>
      ),
    },
    {
      key: "reason",
      label: t("log.reason"),
      fields: ["reason", "status"],
      // The reason, and nothing else. Naming the changed fields here as well
      // crowded out the one thing the column is for — and Type of Modification
      // beside it already lists every one of them, against its own before and
      // after.
      render: (r, lookup) => (
        <span className="block min-w-0">
          <span className="text-fg block">{lookup("reason", r.reason)}</span>
          <span className="mt-1 flex flex-wrap items-center gap-2">
            <Badge tone={TONE[r.status]}>{lookup("status", r.status)}</Badge>
          </span>
        </span>
      ),
    },
    // The change as three aligned columns of boxes: what, from, to.
    {
      key: "modification",
      label: t("log.modification"),
      fields: ["changes"],
      className: "min-w-44",
      render: (r) => <Boxes values={changedKeys(r).map(title)} />,
    },
    {
      key: "from",
      label: t("log.from"),
      fields: ["changes"],
      className: "min-w-44",
      render: (r) => <Boxes values={changedKeys(r).map((k) => r.changes?.before[k] ?? null)} />,
    },
    {
      key: "to",
      label: t("log.to"),
      fields: ["changes"],
      className: "min-w-44",
      render: (r) => <Boxes values={changedKeys(r).map((k) => r.changes?.after[k] ?? null)} />,
    },
    // Who did it: the handler when one acted, otherwise the account itself; and when.
    {
      key: "actor",
      label: t("log.actor"),
      fields: ["handle_user", "system_user", "created_at"],
      render: (r) => (
        <span className="block min-w-0">
          <span className="text-fg block">{r.handle_user}</span>
          <span className="text-fg-muted text-footnote block whitespace-nowrap">
            {format(new Date(r.created_at), "MMM d, yyyy, h:mm a")}
          </span>
        </span>
      ),
    },
  ],
  filters: [
    { name: "reason", label: t("log.reason"), type: "select", loadOptions: label("reason") },
    { name: "status", label: t("log.status"), type: "select", loadOptions: label("status") },
    {
      name: "system_user_id",
      label: t("log.employee"),
      type: "fk",
      endpoint: "/users/compact",
    },
    {
      name: "handle_user_id",
      label: t("log.handledBy"),
      type: "fk",
      endpoint: "/users/compact",
    },
    // A window reads only the months it covers; the table holds every event ever raised.
    { name: "date_from", label: t("log.fromDate"), type: "date" },
    { name: "date_to", label: t("log.toDate"), type: "date" },
  ],
  fields: [],
  toForm: () => ({}),
  toolbar: <ExportButton />,
});

/** A change-set key as a reader would say it. */
const title = (key: string) => key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

/** What actually happened, in words: each changed field if any, else the note. */
const changedKeys = (entry: LogEntry) => Object.keys(entry.changes?.after ?? {});

/** One muted box per value, stacked; the three change columns share the same count, so rows align. */
function Boxes({ values }: { values: (string | null)[] }) {
  const shown = values.length ? values : [null];
  return (
    <span className="grid gap-1.5">
      {shown.map((value, index) => (
        <span
          key={index}
          className="bg-surface-inset text-fg text-callout block truncate rounded-lg px-3 py-1.5"
        >
          {value ?? "—"}
        </span>
      ))}
    </span>
  );
}

/** The current page's rows as a CSV file, built in the browser. */
function ExportButton() {
  const t = useTranslation();
  const download = async () => {
    const page = await listCursor<LogEntry>("/employee-activity-logs", { size: 100 });
    const head = [
      t("log.employeeNo"),
      t("log.name"),
      t("log.reason"),
      t("log.date"),
      t("log.status"),
      t("log.actor"),
    ];
    const rows = page.items.map((r) => [
      r.employee_no,
      r.full_name,
      r.reason,
      r.created_at,
      r.status,
      r.handle_user,
    ]);
    const csv = [head, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = Object.assign(document.createElement("a"), {
      href: url,
      download: "activity-log.csv",
    });
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Button variant="secondary" onClick={() => void download()}>
      <Download aria-hidden /> {t("rp.export")}
    </Button>
  );
}

function ActivityLogPage() {
  const t = useTranslation();
  const config = useMemo(() => build(t), [t]);
  // Warm the option labels so the first paint already has words, not codes.
  useQuery({
    queryKey: ["employee-activity-logs", "options"],
    queryFn: ({ signal }) => options(signal),
    staleTime: 300_000,
  });
  return <ResourcePage config={config} />;
}

export default ActivityLogPage;
